'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState, useCallback, Fragment, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import I18n from 'I18n';
import Loadable from 'UIComponents/decorators/Loadable';
import UIClickable from 'UIComponents/button/UIClickable';
import UICarousel from 'UIComponents/carousel/UICarousel';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UILightbox from 'UIComponents/lightbox/UILightbox';
import UIImage from 'UIComponents/image/UIImage';
import videoThumbOverlay from 'bender-url!../../../../img/video-thumb-overlay.png';
import { useMounted } from '../../../utils/hooks/useMounted'; // https://git.hubteam.com/HubSpot/UIComponents/blob/ed2f3cb96e13c58d9390c3a0be067a18acbbd6bc/UIComponents/static-3.1835/js/carousel/UICarousel.js#L32

const MAX_STEPS = 5;
const AsyncVideoPlayer = Loadable({
  loader: () => import(
  /* webpackChunkName: 'video-player' */
  './VideoPlayer')
});

const Carousel = ({
  onTrackInteraction,
  screenshots,
  videoUrl
}) => {
  const mounted = useMounted();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepOffset, setStepOffset] = useState(0);
  const [videoOverlay, setVideoOverlay] = useState(null);
  useLayoutEffect(() => {
    if (videoUrl) {
      const overlay = new Image();
      overlay.src = videoThumbOverlay;
      overlay.alt = I18n.text('appMarketplace.detail.overview.video.prompt');
      overlay.style.cssText = 'position: absolute; top: 0; left: 0; z-index: 1; width: 100%;';
      overlay.id = 'carousel-video-overlay';

      overlay.onload = () => {
        const overlayParent = document.querySelector('.with-video footer > div > div:first-child div:first-child div');

        if (overlayParent && mounted) {
          overlayParent.appendChild(overlay);
          setVideoOverlay(overlay);
        }
      };
    }
  }, [mounted, videoUrl]);
  const [immutableSteps, totalSteps] = useMemo(() => {
    let steps = screenshots.map(screenshot => ({
      alt: screenshot.get('altText'),
      image: screenshot.get('imageUrl')
    }));

    if (videoUrl) {
      steps = steps.unshift({
        alt: videoUrl,
        image: `${videoUrl}\\thumb.jpg`
      });
    }

    return [steps, steps.size];
  }, [screenshots, videoUrl]);
  const onStepIndexChange = useCallback(({
    target: {
      value
    }
  }) => {
    const adjustedStepIndex = value + stepOffset;
    let nextStepIndex;
    let nextStepOffset;

    if (adjustedStepIndex === stepOffset && stepOffset > 0) {
      nextStepOffset = stepOffset - MAX_STEPS + 2;
      nextStepIndex = MAX_STEPS - 2;
    } else if (adjustedStepIndex === stepOffset + MAX_STEPS - 1 && adjustedStepIndex < totalSteps - 1) {
      nextStepOffset = stepOffset + MAX_STEPS - 2;
      nextStepIndex = 1;
    } else {
      nextStepOffset = stepOffset;
      nextStepIndex = value;
    }

    setStepIndex(nextStepIndex);
    setStepOffset(nextStepOffset);

    if (videoOverlay) {
      videoOverlay.style.display = adjustedStepIndex <= 3 ? 'block' : 'none';
    }
  }, [setStepIndex, stepOffset, totalSteps, videoOverlay]);
  const renderedLightbox = useMemo(() => {
    const step = immutableSteps.get(stepIndex + stepOffset);

    if (!(lightboxOpen && step && step.image)) {
      return null;
    }

    return /*#__PURE__*/_jsx(UILightbox, {
      imageAlt: step.alt,
      imageSrc: step.image,
      onClose: () => setLightboxOpen(false),
      children: /*#__PURE__*/_jsx(UIButtonWrapper, {
        children: step.alt
      })
    });
  }, [immutableSteps, stepIndex, stepOffset, lightboxOpen, setLightboxOpen]);
  const MainContent = useCallback(({
    children
  }) => {
    return children.props.alt && children.props.alt.indexOf('.mp4') !== -1 ? /*#__PURE__*/_jsx(AsyncVideoPlayer, {
      onTrackInteraction: onTrackInteraction,
      videoUrl: children.props.alt
    }) : /*#__PURE__*/_jsx(UIClickable, {
      onClick: () => {
        setLightboxOpen(true);
        onTrackInteraction('click carousel screenshot', 'overview');
      },
      children: children
    });
  }, [setLightboxOpen, onTrackInteraction]);
  const singleClickableField = useMemo(() => {
    const item = immutableSteps.first();

    if (!item) {
      return null;
    }

    return item.alt && item.alt.indexOf('.mp4') !== -1 ? /*#__PURE__*/_jsx(AsyncVideoPlayer, {
      videoUrl: item.alt
    }) : /*#__PURE__*/_jsx(UIClickable, {
      onClick: () => {
        setLightboxOpen(true);
        onTrackInteraction('click carousel screenshot', 'overview');
      },
      children: /*#__PURE__*/_jsx(UIImage, {
        alt: item.alt || '',
        src: item.image,
        style: {
          margin: 'auto'
        }
      })
    });
  }, [immutableSteps, setLightboxOpen, onTrackInteraction]);

  if (immutableSteps.isEmpty()) {
    return null;
  }

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [immutableSteps.size > 1 ? /*#__PURE__*/_jsx(UICarousel, {
      className: videoUrl && 'with-video',
      MainContent: MainContent,
      onStepIndexChange: onStepIndexChange,
      stepIndex: stepIndex,
      steps: immutableSteps.slice(stepOffset, stepOffset + MAX_STEPS).toArray(),
      style: {
        marginTop: -20
      },
      use: "max"
    }) : /*#__PURE__*/_jsx("div", {
      className: "m-top-6",
      children: singleClickableField
    }), renderedLightbox]
  });
};

Carousel.propTypes = {
  onTrackInteraction: PropTypes.func.isRequired,
  screenshots: PropTypes.instanceOf(Immutable.List).isRequired,
  videoUrl: PropTypes.string
};
export default Carousel;